import { Route, Routes } from "react-router-dom";
import { Logout } from "./components/Logout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { useAuth } from "./hooks/useAuth";
import { useSubscription } from "./hooks/useSubscription";
import { ActionPlanPage } from "./pages/ActionPlanPage";
import { AuthCallbackPage } from "./pages/AuthCallbackPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DemoLoginPage } from "./pages/DemoLoginPage";
import { DiversityRatioPage } from "./pages/DiversityRatioPage";
import { ErrorPage } from "./pages/ErrorPage";
import { LoginPage } from "./pages/LoginPage";
import { PayEquityPage } from "./pages/PayEquityPage";
import { PipelinePage } from "./pages/PipelinePage";
import { ReportPage } from "./pages/ReportPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SignUpPage } from "./pages/SignUpPage";
import { SurveyDispatchOverviewPage } from "./pages/SurveyDispatchOverviewPage";
import { SurveyLaunchPage } from "./pages/SurveyLaunchPage";
import { SurveyPage } from "./pages/SurveyPage";
import { SurveyPreviewPage } from "./pages/SurveyPreviewPage";
import { SurveyRedirectPage } from "./pages/SurveyRedirectPage";
import { SurveySelectionPage } from "./pages/SurveySelectionPage";
import { TrainingCompletionPage } from "./pages/TrainingCompletionPage";
import { TrainingPage } from "./pages/TrainingPage";
import { WorkplaceCultureChapterPage } from "./pages/WorkplaceCultureChapterPage";
import { WorkplaceCulturePage } from "./pages/WorkplaceCulturePage";
import { SubscriptionActions } from "./types/user";
import {
  ProtectedRoutes,
  PublicRoutes,
  SurveyRoutes,
} from "./utils/routes.utils";

const Frame = () => {
  const { authInfo } = useAuth();
  const { hasAccess } = useSubscription();

  // Change default to false after all users have logged in after this change as it will return false if users have not logged in again after this change
  const hasAccessToIntegrations = authInfo.user
    ? hasAccess(SubscriptionActions.HR_INTEGRATION)
    : true;
  const hasAccessToLaunchSurvey = authInfo.user
    ? hasAccess(SubscriptionActions.LAUNCH_SURVEY)
    : true;

  const hasAccessToActionPlan = authInfo.user
    ? hasAccess(SubscriptionActions.ACTION_PLAN)
    : true;

  return (
    <>
      <Routes>
        <Route
          index
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route path={PublicRoutes.SIGNUP} element={<SignUpPage />} />
        <Route path={PublicRoutes.LOGIN}>
          <Route path="" element={<LoginPage />} />
          <Route path=":tenantLoginId" element={<LoginPage />} />
        </Route>
        <Route
          path={PublicRoutes.AUTH_CALLBACK}
          element={<AuthCallbackPage />}
        />
        {import.meta.env.VITE_FEATURE_DEMO_AUTHENTICATION === "true" && (
          <Route path={PublicRoutes.DEMO_LOGIN} element={<DemoLoginPage />} />
        )}
        <Route path={PublicRoutes.LOGOUT} element={<Logout />} />
        <Route
          path={PublicRoutes.TRAINING_COMPLETION}
          element={<TrainingCompletionPage />}
        />
        <Route
          path={ProtectedRoutes.DASHBOARD}
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        {hasAccessToLaunchSurvey && (
          <>
            <Route
              path={ProtectedRoutes.LAUNCH}
              element={
                <ProtectedRoute>
                  <SurveySelectionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ProtectedRoutes.LAUNCH + "/:dispatchId"}
              element={
                <ProtectedRoute>
                  <SurveyLaunchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ProtectedRoutes.WORKPLACE_CULTURE}
              element={
                <ProtectedRoute>
                  <SurveyDispatchOverviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ProtectedRoutes.WORKPLACE_CULTURE + "/:dispatchId"}
              element={
                <ProtectedRoute>
                  <WorkplaceCulturePage />
                </ProtectedRoute>
              }
            />

            <Route
              path={
                ProtectedRoutes.WORKPLACE_CULTURE +
                "/:dispatchId" +
                ProtectedRoutes.CHAPTER +
                "/:chapterId"
              }
              element={
                <ProtectedRoute>
                  <WorkplaceCultureChapterPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={
                ProtectedRoutes.WORKPLACE_CULTURE +
                "/:dispatchId" +
                ProtectedRoutes.REPORT
              }
              element={
                <ProtectedRoute>
                  <ReportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={`${SurveyRoutes.PREVIEW_SURVEY}/:id`}
              element={<SurveyPreviewPage />}
            />
          </>
        )}
        {hasAccessToIntegrations && (
          <>
            <Route
              path={ProtectedRoutes.DIVERSITY_RATIO}
              element={
                <ProtectedRoute>
                  <DiversityRatioPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ProtectedRoutes.PAY_EQUITY}
              element={
                <ProtectedRoute>
                  <PayEquityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ProtectedRoutes.PIPELINE}
              element={
                <ProtectedRoute>
                  <PipelinePage />
                </ProtectedRoute>
              }
            />
          </>
        )}

        {hasAccessToActionPlan && (
          <Route
            path={ProtectedRoutes.ACTION_PLAN}
            element={
              <ProtectedRoute>
                <ActionPlanPage />
              </ProtectedRoute>
            }
          />
        )}

        <Route
          path={ProtectedRoutes.SETTINGS}
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ProtectedRoutes.TRAINING}
          element={
            <ProtectedRoute>
              <TrainingPage />
            </ProtectedRoute>
          }
        />

        <Route path={SurveyRoutes.START_SURVEY} element={<SurveyPage />} />
        <Route
          path={SurveyRoutes.SHORT_SURVEY}
          element={<SurveyRedirectPage />}
        />
        <Route path="*" element={<ErrorPage errorCode={404} />} />
      </Routes>
    </>
  );
};

export default Frame;
